/**
 * OAuth Debugger Component
 * 
 * This component provides detailed debugging information about OAuth configuration
 * and credential loading. It can be toggled on/off and works in both development
 * and production environments.
 */

import React, { useState, useEffect } from 'react';
import { 
  Box, Button, Heading, Text, Divider, Badge, Code,
  Accordion, AccordionItem, AccordionButton, AccordionPanel, AccordionIcon,
  List, ListItem, ListIcon, VStack 
} from '@chakra-ui/react';
import type { IconProps, ComponentWithAs } from '@chakra-ui/react';
import { MdCheckCircle, MdError } from 'react-icons/md';
import { isProduction, getEnv, getRedirectUri } from '../utils/environment';
import { getDiscordClientId, getGoogleClientId } from '../utils/backendConfig';
import { useAuth } from '../context/AuthContext';

/**
 * Provides comprehensive OAuth debugging information
 */
export const OAuthDebugger: React.FC = () => {
  const { userInfo, checkScreenNameAvailability } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [discordStatus, setDiscordStatus] = useState({ available: false, id: '' });
  const [googleStatus, setGoogleStatus] = useState({ available: false, id: '' });
  const [screenNameStatus, setScreenNameStatus] = useState({
    hasScreenName: false,
    canChange: false,
    needsScreenName: false,
    apiResponsive: false,
    validationWorking: false,
    profanityFilterWorking: false
  });
  const [diagnosticsLogs, setDiagnosticsLogs] = useState<Array<{
    timestamp: string;
    message: string;
    data?: string;
  }>>([]);
  const [flowSteps, setFlowSteps] = useState<Array<{
    step: 'initiate' | 'redirect' | 'callback' | 'complete';
    timestamp: string;
    data?: any;
  }>>([]);

  const [apiLoadMetrics, setApiLoadMetrics] = useState<{
    startTime?: number;
    endTime?: number;
    success: boolean;
    statusCode?: number;
  }>();

  const [uriValidation, setUriValidation] = useState<{
    configured: string;
    actual: string;
    matches: boolean;
  }>();

  const [showDebug, setShowDebug] = useState(false);
  const toggleDebug = () => setShowDebug(!showDebug);

  // Load all diagnostic data
  useEffect(() => {
    const loadCredentials = async () => {
      try {
        // Check client IDs from backend config system
        const discordId = await getDiscordClientId();
        const googleId = await getGoogleClientId();
        
        const discordStatus = {
          available: !!discordId,
          id: discordId ? discordId.substring(0, 6) + '...' : 'Missing'
        };
        
        const googleStatus = {
          available: !!googleId,
          id: googleId ? googleId.substring(0, 6) + '...' : 'Missing'
        };
        
        setDiscordStatus(discordStatus);
        setGoogleStatus(googleStatus);
        
        // Test screen name system functionality
        await testScreenNameSystem();
      } catch (error) {
        console.error('[OAuthDebugger] Error loading credentials:', error);
        setDiscordStatus({ available: false, id: 'Error loading' });
        setGoogleStatus({ available: false, id: 'Error loading' });
      }
    };

    // Test screen name system functionality
    const testScreenNameSystem = async () => {
      try {
        // Test API availability check
        const availabilityTest = await checkScreenNameAvailability('test_availability_check_12345');
        const apiResponsive = availabilityTest !== null;
        
        console.log('[OAuthDebugger] API test:', availabilityTest);
        
        // Test profanity filter
        const profanityTest = await checkScreenNameAvailability('badword123');
        const profanityFilterWorking = profanityTest && !profanityTest.available; // Should be blocked
        
        console.log('[OAuthDebugger] Profanity test:', profanityTest);
        
        // Test validation
        const validationTest = await checkScreenNameAvailability('x'); // Too short
        const validationWorking = validationTest && !validationTest.valid; // Should fail validation
        
        console.log('[OAuthDebugger] Validation test:', validationTest);
        
        setScreenNameStatus({
          hasScreenName: !!userInfo?.screenName,
          canChange: !!userInfo?.canChangeScreenName,
          needsScreenName: !!userInfo?.needsScreenName,
          apiResponsive,
          validationWorking,
          profanityFilterWorking
        });
      } catch (error) {
        console.error('[OAuthDebugger] Error testing screen name system:', error);
        setScreenNameStatus({
          hasScreenName: false,
          canChange: false,
          needsScreenName: false,
          apiResponsive: false,
          validationWorking: false,
          profanityFilterWorking: false
        });
      }
    };

    loadCredentials();
    
    // Initialize empty diagnostics logs (old window-based system removed)
    setDiagnosticsLogs([]);

    // Track API load metrics
    const loadStart = Date.now();
    
    // API load metrics now tracked through backend config system
    setApiLoadMetrics({
      startTime: loadStart,
      endTime: Date.now(),
      success: true, // Will be updated based on actual backend config loading
      statusCode: 200
    });

    // Check redirect URIs
    setUriValidation({
      configured: getRedirectUri('discord'),
      actual: window.location.href,
      matches: window.location.href.startsWith(getRedirectUri('discord'))
    });

    // Initial flow step
    setFlowSteps([{
      step: 'initiate',
      timestamp: new Date().toISOString()
    }]);
  }, [checkScreenNameAvailability, userInfo?.canChangeScreenName, userInfo?.needsScreenName, userInfo?.screenName]);
  
  // Generate environment information
  const envInfo = {
    isProduction: isProduction(),
    hostname: window.location.hostname,
    origin: window.location.origin,
    discordEnvVar: !!getEnv('REACT_APP_DISCORD_CLIENT_ID', ''),
    googleEnvVar: !!getEnv('REACT_APP_GOOGLE_CLIENT_ID', ''),
    adminIds: getEnv('REACT_APP_ADMIN_DISCORD_IDS', '').split(',').filter(Boolean),
    adminEmails: getEnv('REACT_APP_ADMIN_EMAILS', '').split(',').filter(Boolean)
  };

  // Only show in development or with debug flag
  if (isProduction() && !localStorage.getItem('MYTHBOUND_DEBUG')) {
    return null;
  }

  return (
    <Box position="fixed" bottom="0" right="0" zIndex={1000} m={4} maxW="600px">
      <Accordion allowToggle>
        <AccordionItem border="none">
          <AccordionButton px={0}>
            <Box flex="1" textAlign="left">
              <Heading size="sm">OAuth Debugger</Heading>
            </Box>
            <AccordionIcon />
          </AccordionButton>
          <AccordionPanel pb={4}>
            <Button 
              size="sm" 
              colorScheme="blue" 
              onClick={toggleDebug} 
              mb={4}
            >
              {showDebug ? 'Hide' : 'Show'} OAuth Debug
            </Button>
            
            {showDebug && (
              <Box mt={2}>
                <Box 
                  p={4} 
                  bg="gray.800" 
                  color="white" 
                  borderRadius="md" 
                  boxShadow="lg"
                  overflowY="auto"
                  maxH="80vh"
                >
                  <Heading size="md" mb={2}>Mythbound OAuth Debugger</Heading>
                  <Text fontSize="sm" color="gray.400">Environment: {envInfo.isProduction ? 'Production' : 'Development'}</Text>
                  <Text fontSize="sm" color="gray.400">Host: {envInfo.hostname}</Text>
                  
                  <Divider my={3} />
                  
                  <Heading size="sm" mb={2}>Credential Status</Heading>
                  <List spacing={2}>
                    <ListItem>
                      <ListIcon 
                        as={MdCheckCircle as ComponentWithAs<'svg', IconProps>}
                        color={discordStatus.available ? "green.500" : "red.500"} 
                      />
                      Discord Client ID: {discordStatus.id}
                      {discordStatus.available && <Badge ml={2} colorScheme="green">Available</Badge>}
                      {!discordStatus.available && <Badge ml={2} colorScheme="red">Missing</Badge>}
                    </ListItem>
                    <ListItem>
                      <ListIcon 
                        as={MdError as ComponentWithAs<'svg', IconProps>}
                        color={googleStatus.available ? "green.500" : "red.500"} 
                      />
                      Google Client ID: {googleStatus.id}
                      {googleStatus.available && <Badge ml={2} colorScheme="green">Available</Badge>}
                      {!googleStatus.available && <Badge ml={2} colorScheme="red">Missing</Badge>}
                    </ListItem>
                  </List>
                  
                  <Divider my={3} />
                  
                  <Heading size="sm" mb={2}>Admin Configuration</Heading>
                  <Text fontSize="sm">Discord IDs: {envInfo.adminIds.length > 0 ? envInfo.adminIds.join(', ') : 'None configured'}</Text>
                  <Text fontSize="sm">Admin Emails: {envInfo.adminEmails.length > 0 ? envInfo.adminEmails.join(', ') : 'None configured'}</Text>
                  
                  <Divider my={3} />
                  
                  <Heading size="sm" mb={2}>Screen Name Management System</Heading>
                  <List spacing={2}>
                    <ListItem>
                      <ListIcon 
                        as={MdCheckCircle as ComponentWithAs<'svg', IconProps>}
                        color={userInfo ? "green.500" : "red.500"} 
                      />
                      User Context: {userInfo ? 'Available' : 'Not Available'}
                      {userInfo && <Badge ml={2} colorScheme="green">Connected</Badge>}
                      {!userInfo && <Badge ml={2} colorScheme="red">Disconnected</Badge>}
                    </ListItem>
                    <ListItem>
                      <ListIcon 
                        as={MdCheckCircle as ComponentWithAs<'svg', IconProps>}
                        color={screenNameStatus.hasScreenName ? "green.500" : "orange.500"} 
                      />
                      Screen Name Status: {screenNameStatus.hasScreenName ? `"${userInfo?.screenName}"` : 'Not Set'}
                      {screenNameStatus.hasScreenName && <Badge ml={2} colorScheme="green">Set</Badge>}
                      {!screenNameStatus.hasScreenName && screenNameStatus.needsScreenName && <Badge ml={2} colorScheme="orange">Required</Badge>}
                      {!screenNameStatus.hasScreenName && !screenNameStatus.needsScreenName && <Badge ml={2} colorScheme="gray">Optional</Badge>}
                    </ListItem>
                    <ListItem>
                      <ListIcon 
                        as={MdCheckCircle as ComponentWithAs<'svg', IconProps>}
                        color={screenNameStatus.canChange ? "green.500" : "red.500"} 
                      />
                      Change Permission: {screenNameStatus.canChange ? 'Allowed' : 'Not Allowed'}
                      {screenNameStatus.canChange && <Badge ml={2} colorScheme="green">Available</Badge>}
                      {!screenNameStatus.canChange && <Badge ml={2} colorScheme="red">Restricted</Badge>}
                    </ListItem>
                    <ListItem>
                      <ListIcon 
                        as={MdCheckCircle as ComponentWithAs<'svg', IconProps>}
                        color={screenNameStatus.apiResponsive ? "green.500" : "red.500"} 
                      />
                      API Connectivity: {screenNameStatus.apiResponsive ? 'Working' : 'Failed'}
                      {screenNameStatus.apiResponsive && <Badge ml={2} colorScheme="green">Responsive</Badge>}
                      {!screenNameStatus.apiResponsive && <Badge ml={2} colorScheme="red">Error</Badge>}
                    </ListItem>
                    <ListItem>
                      <ListIcon 
                        as={MdCheckCircle as ComponentWithAs<'svg', IconProps>}
                        color={screenNameStatus.validationWorking ? "green.500" : "red.500"} 
                      />
                      Input Validation: {screenNameStatus.validationWorking ? 'Working' : 'Failed'}
                      {screenNameStatus.validationWorking && <Badge ml={2} colorScheme="green">Active</Badge>}
                      {!screenNameStatus.validationWorking && <Badge ml={2} colorScheme="red">Broken</Badge>}
                    </ListItem>
                    <ListItem>
                      <ListIcon 
                        as={MdCheckCircle as ComponentWithAs<'svg', IconProps>}
                        color={screenNameStatus.profanityFilterWorking ? "green.500" : "red.500"} 
                      />
                      Profanity Filter: {screenNameStatus.profanityFilterWorking ? 'Working' : 'Failed'}
                      {screenNameStatus.profanityFilterWorking && <Badge ml={2} colorScheme="green">Active</Badge>}
                      {!screenNameStatus.profanityFilterWorking && <Badge ml={2} colorScheme="red">Broken</Badge>}
                    </ListItem>
                  </List>
                  
                  {userInfo && (
                    <Box mt={3} p={3} bg="gray.700" borderRadius="md">
                      <Text fontSize="xs" color="gray.300" fontWeight="bold">User Details:</Text>
                      <Text fontSize="xs">ID: {userInfo.id?.substring(0, 8) || 'N/A'}</Text>
                      <Text fontSize="xs">Screen Name: {userInfo.screenName || 'Not set'}</Text>
                      <Text fontSize="xs">Needs Screen Name: {userInfo.needsScreenName ? 'Yes' : 'No'}</Text>
                      <Text fontSize="xs">Can Change: {userInfo.canChangeScreenName ? 'Yes' : 'No'}</Text>
                      <Text fontSize="xs">Roles: {userInfo.roles?.join(', ') || 'Unknown'}</Text>
                    </Box>
                  )}
                  
                  <Divider my={3} />
                  
                  <Accordion allowToggle>
                    <AccordionItem border="none">
                      <AccordionButton px={0}>
                        <Box flex="1" textAlign="left">
                          <Heading size="sm">Diagnostic Logs ({diagnosticsLogs.length})</Heading>
                        </Box>
                        <AccordionIcon />
                      </AccordionButton>
                      <AccordionPanel pb={4} px={0}>
                        {diagnosticsLogs.map((log, index) => (
                          <Box 
                            key={index} 
                            mb={2} 
                            p={2} 
                            bg="gray.700" 
                            borderRadius="md"
                            fontSize="xs"
                          >
                            <Text color="gray.400">{log.timestamp}</Text>
                            <Text fontWeight="bold">{log.message}</Text>
                            {log.data && (
                              <Code 
                                display="block" 
                                whiteSpace="pre" 
                                p={2} 
                                mt={1} 
                                fontSize="xs"
                                bg="gray.900"
                              >
                                {log.data}
                              </Code>
                            )}
                          </Box>
                        ))}
                        {diagnosticsLogs.length === 0 && (
                          <Text fontSize="sm" color="gray.400">No diagnostic logs available</Text>
                        )}
                      </AccordionPanel>
                    </AccordionItem>

                    <AccordionItem border="none">
                      <AccordionButton px={0}>
                        <Box flex="1" textAlign="left">
                          <Heading size="sm">OAuth Flow Steps</Heading>
                        </Box>
                        <AccordionIcon />
                      </AccordionButton>
                      <AccordionPanel pb={4} px={0}>
                        <List spacing={2}>
                          {flowSteps.map((step, i) => (
                            <ListItem key={i}>
                              <Badge>{step.step}</Badge>
                              <Text fontSize="xs">{step.timestamp}</Text>
                              {step.data && <Code>{JSON.stringify(step.data)}</Code>}
                            </ListItem>
                          ))}
                        </List>
                      </AccordionPanel>
                    </AccordionItem>

                    <AccordionItem border="none">
                      <AccordionButton px={0}>
                        <Box flex="1" textAlign="left">
                          <Heading size="sm">Network Diagnostics</Heading>
                        </Box>
                        <AccordionIcon />
                      </AccordionButton>
                      <AccordionPanel pb={4} px={0}>
                        {apiLoadMetrics ? (
                          <VStack align="start" spacing={2}>
                            <Text>Source: Backend API</Text>
                            <Text>Duration: {apiLoadMetrics.endTime && apiLoadMetrics.startTime 
                              ? `${apiLoadMetrics.endTime - apiLoadMetrics.startTime}ms` 
                              : 'N/A'}</Text>
                            <Text>Status: 
                              <Badge colorScheme={apiLoadMetrics.success ? 'green' : 'red'}>
                                {apiLoadMetrics.success ? 'Success' : 'Failed'}
                              </Badge>
                            </Text>
                            <Text>Status Code: {apiLoadMetrics.statusCode}</Text>
                          </VStack>
                        ) : (
                          <Text>No network activity recorded</Text>
                        )}
                      </AccordionPanel>
                    </AccordionItem>

                    <AccordionItem border="none">
                      <AccordionButton px={0}>
                        <Box flex="1" textAlign="left">
                          <Heading size="sm">Redirect URI Check</Heading>
                        </Box>
                        <AccordionIcon />
                      </AccordionButton>
                      <AccordionPanel pb={4} px={0}>
                        {uriValidation && (
                          <VStack align="start" spacing={2}>
                            <Text>Configured: 
                              <Code>{uriValidation.configured}</Code>
                            </Text>
                            <Text>Current: 
                              <Code>{uriValidation.actual}</Code>
                            </Text>
                            <Text>Match: 
                              <Badge colorScheme={uriValidation.matches ? 'green' : 'red'}>
                                {uriValidation.matches ? 'Valid' : 'Mismatch'}
                              </Badge>
                            </Text>
                          </VStack>
                        )}
                      </AccordionPanel>
                    </AccordionItem>
                  </Accordion>
                  
                  <Box mt={3}>
                    <Text fontSize="xs" color="gray.500">
                      This debugger is only visible in development mode or when MYTHBOUND_DEBUG is enabled in localStorage.
                      To enable in production, open the console and run: localStorage.setItem('MYTHBOUND_DEBUG', 'true')
                    </Text>
                  </Box>
                </Box>
              </Box>
            )}
          </AccordionPanel>
        </AccordionItem>
      </Accordion>
    </Box>
  );
};

export default OAuthDebugger;
